import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// API service for all backend calls
class ApiService {
  constructor() {
    this.baseURL = BACKEND_URL;
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Spots v2 endpoints - Nouveau modèle de données
  async getSpots() {
    try {
      const response = await this.client.get('/api/spots-v2');
      // Transformer les données v2 pour compatibilité avec le frontend existant
      return response.data.map(spot => this.transformSpotV2ToLegacy(spot));
    } catch (error) {
      console.error('Error fetching spots:', error);
      throw error;
    }
  }

  async getSpotById(spotId) {
    try {
      const response = await this.client.get(`/api/spots-v2/${spotId}`);
      // L'API renvoie { spot, photos }
      const transformed = this.transformSpotV2ToLegacy(response.data.spot);
      transformed.photos = response.data.photos?.map(photo => ({
        url: photo.cdn_url,
        metadata: {
          title: photo.caption,
          date: photo.taken_at ? new Date(photo.taken_at).toLocaleDateString('fr-FR') : null,
          weather: photo.photo_conditions?.weather?.join(', '),
          light: photo.photo_conditions?.light?.join(', ')
        },
        author: photo.uploaded_by_user_id
      })) || [];
      return transformed;
    } catch (error) {
      console.error('Error fetching spot:', error);
      throw error;
    }
  }

  async createSpot(spotData) {
    try {
      const token = localStorage.getItem('authToken');
      // Transformer les données du formulaire vers le format v2
      const v2Data = this.transformLegacyToSpotV2(spotData);
      const response = await this.client.post('/api/spots-v2', v2Data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return this.transformSpotV2ToLegacy(response.data);
    } catch (error) {
      console.error('Error creating spot:', error);
      throw error;
    }
  }

  async updateSpot(spotId, spotData) {
    try {
      const token = localStorage.getItem('authToken');
      const v2Data = this.transformLegacyToSpotV2(spotData);
      const response = await this.client.put(`/api/spots-v2/${spotId}`, v2Data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return this.transformSpotV2ToLegacy(response.data);
    } catch (error) {
      console.error('Error updating spot:', error);
      throw error;
    }
  }

  async deleteSpot(spotId) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.client.delete(`/api/spots-v2/${spotId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting spot:', error);
      throw error;
    }
  }

  // Like/Unlike endpoints
  async likeSpot(spotId, action = 'like') {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.client.post(`/api/spots-v2/${spotId}/like`, { action }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error liking spot:', error);
      throw error;
    }
  }

  // Photo Like endpoints
  async likePhoto(photoUrl, spotId) {
    try {
      const token = localStorage.getItem('authToken');
      console.log('likePhoto - Token present:', !!token);
      console.log('likePhoto - Sending request to:', '/api/user/photos/like');
      
      if (!token) {
        console.error('likePhoto - No auth token found!');
        throw new Error('No authentication token');
      }
      
      const response = await this.client.post('/api/user/photos/like', 
        { photo_url: photoUrl, spot_id: spotId },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      console.log('likePhoto - Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error liking photo:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  async getLikedPhotos() {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.client.get('/api/user/liked-photos', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching liked photos:', error);
      throw error;
    }
  }

  // Comments endpoints
  async getComments(spotId) {
    try {
      const response = await this.client.get(`/api/spots-v2/${spotId}/comments`);
      return response.data;
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  async addComment(spotId, commentData) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.client.post(
        `/api/spots-v2/${spotId}/comments`,
        null,
        {
          params: { content: commentData.comment },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  // Enums pour le formulaire v2
  async getSpotEnums() {
    try {
      const response = await this.client.get('/api/spots-v2/enums');
      return response.data;
    } catch (error) {
      console.error('Error fetching spot enums:', error);
      throw error;
    }
  }

  // Transformation: Spot v2 -> Format legacy pour compatibilité frontend
  transformSpotV2ToLegacy(spot) {
    if (!spot) return null;
    
    return {
      id: spot.id,
      title: spot.title,
      description: spot.description,
      latitude: spot.spot_lat,
      longitude: spot.spot_lng,
      parking_latitude: spot.parking?.parking_lat,
      parking_longitude: spot.parking?.parking_lng,
      tags: spot.tags || [],
      likes: spot.likes_count || 0,
      comments: [],
      comments_count: spot.comments_count || 0,
      author: spot.created_by_user_id || 'anonyme',
      created_at: spot.created_at,
      // Nouvelles métadonnées v2
      v2_data: {
        recommended_time_windows: spot.recommended_time_windows || [],
        ideal_conditions: spot.ideal_conditions || { weather: [], light: [] },
        crowd_level: spot.crowd_level || 'moderate',
        accessibility: spot.accessibility || { difficulty: 'easy' },
        parking: spot.parking || {},
        moderation_status: spot.moderation_status
      },
      // Photo de couverture
      photos: spot.cover_photo_url ? [{ url: spot.cover_photo_url }] : [],
      // Infos pratiques (compatibilité avec l'ancien format)
      practical_info: {
        recommended_hours: this.formatTimeWindows(spot.recommended_time_windows),
        accessibility: this.formatAccessibility(spot.accessibility),
        occupation_level: this.formatCrowdLevel(spot.crowd_level),
        best_weather: this.formatWeather(spot.ideal_conditions?.weather),
        parking_distance: spot.parking?.parking_distance_m ? `${spot.parking.parking_distance_m}m` : null,
        parking_info: spot.parking?.parking_info
      }
    };
  }

  // Transformation: Format legacy/formulaire -> Spot v2
  transformLegacyToSpotV2(data) {
    // Si c'est déjà au format v2 (depuis AddSpotModalV2)
    if (data.spot_lat !== undefined) {
      return data;
    }
    
    // Sinon, transformer depuis le format legacy
    return {
      title: data.title,
      description: data.description,
      spot_lat: data.latitude,
      spot_lng: data.longitude,
      tags: data.tags || [],
      recommended_time_windows: data.recommended_time_windows || [],
      ideal_conditions: data.ideal_conditions || { weather: [], light: [] },
      crowd_level: data.crowd_level || 'moderate',
      accessibility: data.accessibility || { difficulty: 'easy' },
      parking: {
        parking_lat: data.parking_latitude,
        parking_lng: data.parking_longitude,
        parking_info: data.practical_info?.parking_info
      }
    };
  }

  // Helpers pour le formatage des labels
  formatTimeWindows(windows) {
    if (!windows || windows.length === 0) return null;
    const labels = {
      sunrise: 'Lever de soleil',
      sunset: 'Coucher de soleil',
      morning: 'Matin',
      afternoon: 'Après-midi',
      golden_hour_am: 'Heure dorée (matin)',
      golden_hour_pm: 'Heure dorée (soir)',
      blue_hour_am: 'Heure bleue (matin)',
      blue_hour_pm: 'Heure bleue (soir)',
      night: 'Nuit'
    };
    return windows.map(w => labels[w.type] || w.type).join(', ');
  }

  formatAccessibility(accessibility) {
    if (!accessibility) return null;
    const labels = { easy: 'Facile', moderate: 'Modéré', hard: 'Difficile' };
    let result = labels[accessibility.difficulty] || accessibility.difficulty;
    if (accessibility.walk_time_minutes) {
      result += ` - ${accessibility.walk_time_minutes} min de marche`;
    }
    return result;
  }

  formatCrowdLevel(level) {
    const labels = {
      very_quiet: 'Très calme',
      quiet: 'Calme',
      moderate: 'Modéré',
      popular: 'Populaire',
      crowded: 'Très fréquenté'
    };
    return labels[level] || level;
  }

  formatWeather(weather) {
    if (!weather || weather.length === 0) return null;
    const labels = {
      clear: 'Ciel dégagé',
      partly_cloudy: 'Partiellement nuageux',
      cloudy: 'Nuageux',
      fog: 'Brouillard',
      mist: 'Brume',
      after_rain: 'Après la pluie',
      snow: 'Neige',
      windy: 'Venteux'
    };
    return weather.map(w => labels[w] || w).join(', ');
  }

  // Demo data endpoints
  async initializeDemoData() {
    try {
      const response = await this.client.post('/api/initialize-demo-data');
      return response.data;
    } catch (error) {
      console.error('Error initializing demo data:', error);
      throw error;
    }
  }

  // Tags endpoints
  async getTags(categoryId = null) {
    try {
      const params = categoryId ? { category_id: categoryId } : {};
      const response = await this.client.get('/api/tags', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  async getAllTagsFromSpots() {
    try {
      const response = await this.client.get('/api/tags/all-from-spots');
      return response.data;
    } catch (error) {
      console.error('Error fetching all tags from spots:', error);
      throw error;
    }
  }

  async getTagCategories() {
    try {
      const response = await this.client.get('/api/tag-categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching tag categories:', error);
      throw error;
    }
  }

  async searchTags(query, limit = 10) {
    try {
      const response = await this.client.get('/api/tags/search', {
        params: { q: query, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching tags:', error);
      throw error;
    }
  }

  async searchSpotsByTags(tagSlugs, searchQuery = '') {
    try {
      const response = await this.client.post('/api/spots/search-by-tags', tagSlugs, {
        params: { search_query: searchQuery }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching spots by tags:', error);
      throw error;
    }
  }

  /**
   * Advanced search for spots
   * @param {string} query - Text search query
   * @param {string[]} tags - Array of tag slugs to filter by
   * @param {number} limit - Maximum results
   */
  async searchSpots(query = '', tags = [], limit = 50) {
    try {
      // Use spots-v2 search endpoint
      const response = await this.client.get('/api/spots-v2/search', {
        params: { 
          q: query,
          tags: tags.join(','),
          limit 
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching spots:', error);
      throw error;
    }
  }

  async resetDatabase() {
    try {
      const response = await this.client.delete('/api/reset-db');
      return response.data;
    } catch (error) {
      console.error('Error resetting database:', error);
      throw error;
    }
  }
}

// Create and export a single instance
const apiService = new ApiService();
export default apiService;